import { useState, useEffect, useCallback } from "react";
import { Search, Loader2, Filter } from "lucide-react";
import {
  ClinicDoctorListingItem,
  DoctorAppointmentSettingItem,
} from "./types";
import { DoctorService } from "../../services/api/doctor.service";
import { AppointmentService } from "../../services/api/appointment.service";
import { ClinicSelectionModal } from "./ClinicSelectionModal";
import placeholderAvatar from "../../assets/images/default_avatar.jpg";
import appointmentIllustration from "../../assets/images/find-doctor.svg";
import toast from "react-hot-toast";

interface StepFindDoctorProps {
  onDoctorSelected: (
    doctor: ClinicDoctorListingItem,
    setting: DoctorAppointmentSettingItem
  ) => void;
}

export function StepFindDoctor({ onDoctorSelected }: StepFindDoctorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialization, setSelectedSpecialization] = useState("All");
  const [doctors, setDoctors] = useState<ClinicDoctorListingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItemsCount: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Clinic selection modal
  const [clinicModalOpen, setClinicModalOpen] = useState(false);
  const [selectedDoctorForClinic, setSelectedDoctorForClinic] =
    useState<ClinicDoctorListingItem | null>(null);
  const [doctorSettings, setDoctorSettings] = useState<
    DoctorAppointmentSettingItem[]
  >([]);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Debounce timer
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);

  // Fetch specializations on mount
  useEffect(() => {
    const fetchSpecializations = async () => {
      try {
        const response = await DoctorService.getSpecializationDropdown();
        if (response.success && response.data) {
          setSpecializations(response.data.map((s: any) => s.name || s));
        }
      } catch (err) {
        console.error("Failed to load specializations", err);
      }
    };
    fetchSpecializations();
  }, []);

  // Fetch doctors
  const fetchDoctors = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params: {
          specialization?: string;
          name?: string;
          page: number;
          pageSize: number;
        } = { page, pageSize: 10 };

        if (
          selectedSpecialization &&
          selectedSpecialization !== "All"
        ) {
          params.specialization = selectedSpecialization;
        }
        if (searchQuery.trim()) {
          params.name = searchQuery.trim();
        }

        const response = await DoctorService.getClinicDoctorListing(params);
        if (response.success && response.data) {
          setDoctors(response.data);
          setPagination({
            currentPage: response.pagination?.currentPage || 1,
            totalPages: response.pagination?.totalPages || 1,
            totalItemsCount: response.pagination?.totalItemsCount || 0,
            hasNextPage: response.pagination?.hasNextPage || false,
            hasPreviousPage: response.pagination?.hasPreviousPage || false,
          });
        } else {
          setDoctors([]);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalItemsCount: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          });
        }
      } catch (err) {
        console.error("Failed to load doctors:", err);
        toast.error("Failed to load doctors");
      } finally {
        setLoading(false);
      }
    },
    [selectedSpecialization, searchQuery]
  );

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchDoctors(1);
  }, [selectedSpecialization]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (searchTimer) clearTimeout(searchTimer);
    const timer = setTimeout(() => {
      fetchDoctors(1);
    }, 400);
    setSearchTimer(timer);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle doctor select (fetch appointment settings)
  const handleSelectDoctor = async (doctor: ClinicDoctorListingItem) => {
    setSelectedDoctorForClinic(doctor);
    setLoadingSettings(true);
    try {
      const response = await AppointmentService.getDoctorAppointmentSettings(
        doctor.id
      );
      if (response.success && response.data) {
        const settings = response.data as DoctorAppointmentSettingItem[];

        if (settings.length === 0) {
          toast.error(
            "No appointment options available for this doctor"
          );
          return;
        }

        setDoctorSettings(settings);
        setClinicModalOpen(true);
      } else {
        toast.error("No appointment settings found for this doctor");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch doctor settings");
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleClinicConfirm = (setting: DoctorAppointmentSettingItem) => {
    if (selectedDoctorForClinic) {
      setClinicModalOpen(false);
      onDoctorSelected(selectedDoctorForClinic, setting);
    }
  };

  return (
    <div className="flex gap-6 h-full">
      {/* Left Side — Doctor List (scrollable) */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header: Title + Search (sticky) */}
        <div className="flex items-center gap-4 mb-4 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 whitespace-nowrap">
            Find Doctor
          </h3>
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Specialization Tabs (sticky) */}
        <div
          className="flex gap-2 overflow-x-auto pb-4 mb-2 flex-shrink-0 scrollbar-hide"
          style={{ scrollbarWidth: "none" }}
        >
          <button
            onClick={() => setSelectedSpecialization("All")}
            className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all border ${selectedSpecialization === "All"
                ? "bg-white text-emerald-600 border-emerald-500 shadow-sm"
                : "bg-white text-gray-600 border-gray-300 hover:border-emerald-400 hover:text-emerald-600"
              }`}
          >
            All specialties
          </button>
          {specializations.map((spec) => (
            <button
              key={spec}
              onClick={() => setSelectedSpecialization(spec)}
              className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all border ${selectedSpecialization === spec
                  ? "bg-white text-emerald-600 border-emerald-500 shadow-sm"
                  : "bg-white text-gray-600 border-gray-300 hover:border-emerald-400 hover:text-emerald-600"
                }`}
            >
              {spec}
            </button>
          ))}
        </div>

        {/* Doctor Cards — Scrollable area */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-3" />
              <p className="text-sm text-gray-500">Loading doctors...</p>
            </div>
          ) : doctors.length > 0 ? (
            <div className="space-y-3 pb-4">
              {doctors.map((doctor) => (
                <div
                  key={doctor.id}
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all bg-white"
                >
                  {/* Avatar */}
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                    <img
                      src={doctor.profileImage || placeholderAvatar}
                      alt={doctor.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = placeholderAvatar;
                      }}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 text-sm">
                      Dr. {doctor.name}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      {doctor.specialization?.join(", ")}
                    </p>
                  </div>

                  {/* Rate & Select */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-500">
                      Starting from{" "}
                      <span className="text-emerald-700 font-bold">
                        ₹{doctor.opRate}
                      </span>
                    </span>
                    <button
                      onClick={() => handleSelectDoctor(doctor)}
                      disabled={
                        loadingSettings &&
                        selectedDoctorForClinic?.id === doctor.id
                      }
                      className="px-5 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 shadow-sm"
                    >
                      {loadingSettings &&
                        selectedDoctorForClinic?.id === doctor.id
                        ? "Loading..."
                        : "Select"}
                    </button>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 pt-4">
                  <button
                    onClick={() =>
                      fetchDoctors(pagination.currentPage - 1)
                    }
                    disabled={!pagination.hasPreviousPage}
                    className="px-3 py-1.5 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.currentPage} of{" "}
                    {pagination.totalPages}
                  </span>
                  <button
                    onClick={() =>
                      fetchDoctors(pagination.currentPage + 1)
                    }
                    disabled={!pagination.hasNextPage}
                    className="px-3 py-1.5 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Filter className="w-10 h-10 text-gray-300 mb-3" />
              <p className="text-sm text-gray-500 text-center">
                No doctors found. Try adjusting your search or filters.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right Side — Fixed Illustration */}
      <div className="hidden lg:flex flex-col items-center justify-center w-[320px] flex-shrink-0">
        <img
          src={appointmentIllustration}
          alt="Find Doctor"
          className="w-full max-w-[300px] h-auto opacity-90 mb-4"
        />
        <p className="text-center text-sm text-gray-500 max-w-[250px]">
          Search or filter by specialty to find an available doctor for this
          patient.
        </p>
      </div>

      {/* Clinic Selection Modal */}
      {selectedDoctorForClinic && (
        <ClinicSelectionModal
          isOpen={clinicModalOpen}
          onClose={() => setClinicModalOpen(false)}
          doctor={selectedDoctorForClinic}
          settings={doctorSettings}
          onConfirm={handleClinicConfirm}
        />
      )}
    </div>
  );
}
